import React from 'react';

/**
 * Top-level crash guard for the whole app.
 *
 * WHY: without a boundary above <App/>, ANY uncaught render error makes React 18
 * unmount the entire tree, leaving only the root background (#cacac2 light sand)
 * — the "blank grey page" users report (e.g. a dashboard widget throwing on a
 * property with a disconnected integration). This turns that into a graceful,
 * recoverable screen instead of nothing.
 *
 * It also captures the error + component stack IN THE UI (collapsible), so a
 * user hitting it can screenshot exactly which component crashed — our fastest
 * remote diagnostic.
 */

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Logged so it's visible in the console / any attached logger.
    console.error('🔴 [AppErrorBoundary] Uncaught render error:', error);
    console.error('🔴 [AppErrorBoundary] Component stack:', errorInfo.componentStack);
    this.setState({ componentStack: errorInfo.componentStack ?? null });
  }

  private handleReload = () => {
    // Hard reload — re-fetches everything and re-runs auth bootstrap.
    window.location.reload();
  };

  private handleSignIn = () => {
    // Clean logout path for the session-expiry case: drop any cached auth +
    // selected-property state, then land on the login page. Best-effort; the
    // reload guarantees a clean slate regardless.
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('sb-') || k === 'user_role' || k === 'user_approved' || k === 'user_awaiting_approval' || k.startsWith('zentrian_selected_property'))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      /* ignore storage errors */
    }
    window.location.href = '/login';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: '#cacac2',
          fontFamily: "'Archivo', system-ui, sans-serif",
          color: '#262626',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            background: '#E0DFDA',
            border: '1px solid #cfcec8',
            borderRadius: 18,
            padding: '28px 24px',
            maxWidth: 420,
            width: '100%',
            boxShadow: '0 1px 3px rgba(20,20,20,0.06)',
          }}
        >
          <div style={{ fontSize: 34, marginBottom: 8 }}>⚠️</div>
          <h1 style={{ fontSize: 19, fontWeight: 700, margin: '0 0 6px' }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 14, color: '#6B6A65', lineHeight: 1.5, margin: '0 0 20px' }}>
            The page couldn’t load. This can happen if your session expired or a
            panel hit unexpected data. Reload to try again, or sign in again.
          </p>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                background: '#262626',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                padding: '11px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleSignIn}
              style={{
                background: 'transparent',
                color: '#262626',
                border: '1px solid #b9b8b1',
                borderRadius: 12,
                padding: '11px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Sign in again
            </button>
          </div>

          {this.state.error && (
            <details style={{ marginTop: 20, textAlign: 'left' }}>
              <summary style={{ fontSize: 12, color: '#9E9D97', cursor: 'pointer' }}>
                Technical details
              </summary>
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: 11,
                  color: '#6B6A65',
                  marginTop: 8,
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                {String(this.state.error?.stack || this.state.error)}
                {this.state.componentStack ? `\n\nComponent stack:${this.state.componentStack}` : ''}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
